// src/components/Customer/PlumberProfile.js
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Star, MapPin, Clock, CheckCircle2} from "lucide-react";

import useCategories from "../../hooks/useCategories";
import { db } from "../../firebase";
import { doc, getDoc, collection, getDocs, query, where, orderBy } from "firebase/firestore";

function safeString(v, fallback = "") {
  return typeof v === "string" ? v : fallback;
}
function safeNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function formatReviewDate(createdAt) {
  // createdAt could be Firestore Timestamp or Date-like or null
  const d =
    createdAt?.toDate?.() ||
    (createdAt instanceof Date ? createdAt : null);

  if (!d) return "";
  return d.toLocaleDateString();
}

export default function PlumberProfile() {
  const { id } = useParams(); // taskerId
  const navigate = useNavigate();
  const { categories, loading: categoriesLoading } = useCategories();

  const [tasker, setTasker] = useState(null);
  const [loadingTasker, setLoadingTasker] = useState(true);

  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  // ---- Load tasker from Firestore by params id ----
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoadingTasker(true);
      try {
        if (!id) {
          setTasker(null);
          setLoadingTasker(false);
          return;
        }

        const snap = await getDoc(doc(db, "taskers", id));
        if (cancelled) return;

        if (!snap.exists()) {
          setTasker(null);
          setLoadingTasker(false);
          return;
        }

        setTasker({ id: snap.id, ...snap.data() });
        setLoadingTasker(false);
      } catch {
        if (cancelled) return;
        setTasker(null);
        setLoadingTasker(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // ---- Load reviews from Firestore: reviews where taskerId == id ----
  useEffect(() => {
    let cancelled = false;

    async function loadReviews() {
      setLoadingReviews(true);
      try {
        if (!id) {
          setReviews([]);
          setLoadingReviews(false);
          return;
        }

        const q = query(
          collection(db, "reviews"),
          where("taskerId", "==", id),
          orderBy("createdAt", "desc")
        );

        const snap = await getDocs(q);
        if (cancelled) return;

        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setReviews(list);
        setLoadingReviews(false);
      } catch (e) {
        if (cancelled) return;
        console.error("Load reviews error:", e);
        setReviews([]);
        setLoadingReviews(false);
      }
    }

    loadReviews();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // ---- Services (names) from subcategories ids ----
  const taskerServices = useMemo(() => {
    const subs = Array.isArray(tasker?.subcategories) ? tasker.subcategories : [];
    if (!subs.length) return [];

    const names = [];
    for (const subId of subs) {
      let found = null;
      for (const category of categories || []) {
        const sub = (category?.subcategories || []).find((s) => s.id === subId);
        if (sub) {
          found = sub.name;
          break;
        }
      }
      if (found) names.push(found);
    }
    return names;
  }, [tasker?.subcategories, categories]);

  const computedRating = useMemo(() => {
    if (!reviews?.length) return { avg: 0, count: 0 };
    const sum = reviews.reduce((acc, r) => acc + safeNumber(r.rating, 0), 0);
    return { avg: sum / reviews.length, count: reviews.length };
  }, [reviews]);

  if (loadingTasker || categoriesLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
              <ArrowLeft size={24} />
            </button>
            <h2 className="text-[#1E3A8A]">Tasker Profile</h2>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="bg-white rounded-lg p-6 shadow-sm text-gray-600">Loading…</div>
        </div>
      </div>
    );
  }

  if (!tasker) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
              <ArrowLeft size={24} />
            </button>
            <h2 className="text-[#1E3A8A]">Tasker Profile</h2>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="text-gray-700">Tasker not found</div>
          </div>
        </div>
      </div>
    );
  }

  const name = safeString(tasker.name, "Tasker");
  const imageUrl = safeString(tasker.imageUrl, "") || "/placeholder-avatar.png";
  const availability = safeString(tasker.availability, "—");
  const responseTime = safeString(tasker.responseTime, "—");

  const ratingAvg = Number.isFinite(computedRating.avg) ? computedRating.avg : 0;
  const ratingCount = computedRating.count || 0;

  const priceRangeText =
    tasker?.priceRange && typeof tasker.priceRange === "object"
      ? `${safeNumber(tasker.priceRange.min, 0)} - ${safeNumber(tasker.priceRange.max, 0)} ${safeString(
          tasker.priceRange.currency,
          "SAR"
        )}`
      : safeString(tasker.priceRange, "—");

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-[#1E3A8A]">Tasker Profile</h2>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Profile Header */}
        <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
          <div className="flex items-start gap-4 mb-4">
            <img
              src={imageUrl}
              alt={name}
              className="w-24 h-24 rounded-lg object-cover bg-gray-100"
              onError={(e) => {
                e.currentTarget.src = "/placeholder-avatar.png";
              }}
            />

            <div className="flex-1">
              <h2 className="text-[#1E3A8A] mb-2">{name}</h2>

              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center gap-1">
                  <Star size={20} className="text-yellow-500 fill-yellow-500" />
                  <span>{ratingAvg.toFixed(1)}</span>
                </div>
                <span className="text-gray-400">({ratingCount} reviews)</span>
              </div>

              <div className="flex items-center gap-2 text-gray-600">
                <MapPin size={16} />
                <span>
                  {safeString(tasker.city, "") ? `${tasker.city}, ` : ""}
                  {safeString(tasker.country, "Saudi Arabia")}
                </span>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-[#1E3A8A] mb-3">Services</h3>

            {taskerServices.length ? (
              <div className="flex flex-wrap gap-2 mb-6">
                {taskerServices.map((service) => (
                  <div
                    key={service}
                    className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg"
                  >
                    <CheckCircle2 size={16} className="text-[#006C35]" />
                    <span className="text-[#006C35]">{service}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 mb-6">No services listed.</div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={20} className="text-[#006C35]" />
                <div>
                  <div className="text-sm text-gray-600">Availability</div>
                  <div className="text-[#006C35]">{availability}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Clock size={20} className="text-[#006C35]" />
                <div>
                  <div className="text-sm text-gray-600">Response Time</div>
                  <div className="text-[#006C35]">{responseTime}</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Price Range</div>
              <div className="text-[#1E3A8A]">{priceRangeText}</div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-[#1E3A8A] mb-4">Reviews</h3>

          {loadingReviews ? (
            <div className="text-gray-600 text-sm">Loading reviews…</div>
          ) : reviews.length === 0 ? (
            <div className="text-gray-500 text-sm">No reviews yet.</div>
          ) : (
            <div className="space-y-4">
              {reviews.map((r) => {
                const rating = Math.max(0, Math.min(5, safeNumber(r.rating, 0)));
                return (
                  <div key={r.id} className="border-b border-gray-200 pb-4 last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-800">{safeString(r.customerName, "Customer")}</span>

                      <div className="flex items-center gap-1">
                        {[...Array(rating)].map((_, i) => (
                          <Star key={i} size={16} className="text-yellow-500 fill-yellow-500" />
                        ))}
                        {[...Array(Math.max(0, 5 - rating))].map((_, i) => (
                          <Star key={`e-${i}`} size={16} className="text-gray-200" />
                        ))}
                      </div>
                    </div>

                    {safeString(r.comment, "").trim() ? (
                      <p className="text-gray-600">{r.comment}</p>
                    ) : (
                      <p className="text-gray-400 text-sm">No comment.</p>
                    )}

                    <p className="text-sm text-gray-400 mt-1">{formatReviewDate(r.createdAt)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to= {`/customer/job-details/${tasker.id}`}
            state={{ taskerId: tasker.id }}
            className="block w-full bg-[#006C35] text-white py-4 rounded-lg hover:bg-[#005428] transition-colors text-center"
          >
            Book Now
          </Link>
        </div>
      </div>
    </div>
  );
}